import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import useSEO from '@/hooks/useSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, categoryMap } from '@/mocks/categories';
import { getVideosByCategory, videos } from '@/mocks/videos';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const categoryName = categoryMap[activeSlug] || '全部';
  const filteredVideos = getVideosByCategory(activeSlug);
  const categoryTitle = activeSlug === 'all'
    ? '红桃视频 - 全部影片免费在线观看'
    : `红桃视频${categoryName} - 免费高清在线观看`;

  const sortedVideos = [...filteredVideos].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return b.views - a.views;
    return new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime();
  });

  useSEO({
    title: categoryTitle,
    description: `红桃视频${categoryName}频道提供海量高清${categoryName}资源免费在线观看，无需注册，流畅播放，支持多端同步。`,
    keywords: `红桃视频${categoryName},${categoryName}免费在线观看,高清${categoryName},${categoryName}在线播放`,
    canonicalUrl: `https://hongtao.video/category/${activeSlug}`,
    ogTitle: categoryTitle,
    ogDescription: `红桃视频${categoryName}频道提供海量高清${categoryName}资源免费在线观看`,
    ogUrl: `https://hongtao.video/category/${activeSlug}`,
  });

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), to: '/' },
            { label: t('category.title'), to: '/category/all' },
            { label: categoryName },
          ]}
        />
      </div>

      <div className="bg-background-100/50 py-6">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            {activeSlug === 'all' ? t('category.title') : categoryName}
          </h1>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 hover:bg-primary-100/70 hover:text-primary-700 border border-background-300/60'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-600">
              {t('category.total')} <strong className="text-primary-600">{sortedVideos.length}</strong> {t('category.videos')}
            </p>
            <div className="flex items-center gap-1.5 bg-background-50 rounded-full p-1 border border-background-300/60">
              {([
                { key: 'latest', label: '最新' },
                { key: 'rating', label: '评分' },
                { key: 'views', label: '播放' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {sortedVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {sortedVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无该分类影片</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;